/**
 * The workspace: chat on the left, evidence in the middle-right, artifacts on the rail.
 *
 * Three panes, and only one of them is ever the subject. The evidence panel does not
 * move, collapse or get replaced when you switch between chat, search and entities —
 * that permanence is what makes "check the source" a glance instead of a navigation,
 * and it is why all three left-hand surfaces feed the same selection rather than each
 * growing a viewer of its own.
 *
 * On a phone there is room for one of the three, and it is the subject. The other two
 * become sheets over it: the evidence panel on the citation you tapped, the rail on
 * the `Tables` entry in the topbar's menu. The rail could equally have been dropped
 * — it is a composer for a job that takes minutes — except that it is also the only
 * route to an artifact once one exists, and a phone that cannot open a finished
 * dossier is a phone that cannot read the corpus.
 */

import { useCallback, useMemo, useState } from "react";
import { EvidencePanel } from "../components/EvidencePanel";
import { useSheetMode } from "../lib/sheet";
import { usePublishViewTabs, type ViewTab } from "../state/viewtabs";
import { Chat } from "./Chat";
import { ArtifactRail } from "./ArtifactRail";
import { Search } from "./Search";
import { Entities } from "./Entities";
import { Identities } from "./Identities";
import { Links } from "./Links";
import { Chains } from "./Chains";

type Left = "chat" | "search" | "entities" | "identities" | "links" | "chains";

export function Workspace({ datasetId }: { datasetId: string }) {
  const [left, setLeft] = useState<Left>("chat");
  const [railOpen, setRailOpen] = useState(false);
  const sheet = useSheetMode();
  const [seed, setSeed] = useState<{
    messageId: string;
    question: string;
  } | null>(null);

  // "Build a table from this" hands the composer a question. Where the rail is a sheet
  // that composer is off screen, and a seed with nowhere to land is a button that
  // silently does nothing — so the same act that fills it also surfaces it.
  const onBuildArtifact = useCallback((messageId: string, question: string) => {
    setSeed({ messageId, question });
    setRailOpen(true);
  }, []);
  const onSeedUsed = useCallback(() => setSeed(null), []);
  const closeRail = useCallback(() => setRailOpen(false), []);

  // One description of the strip, rendered in two places and never in both at once:
  // as a row of tabs above the pane on a wide screen, and as the `View` section of
  // the topbar's menu on a phone. Written down once because the alternative was the
  // same six labels, testids and handlers spelled out twice, drifting apart the first
  // time a seventh view is added.
  //
  // `Tables` travels with them but is `selected: null`: it opens the artifact rail
  // over this pane rather than replacing what is in it. It appears in the strip only
  // where the rail is a sheet — above that width the rail is a column that never went
  // away, and a button to reveal it would do nothing.
  const tabs = useMemo<readonly ViewTab[]>(
    () => [
      { testid: "tab-chat", label: "Chat", selected: left === "chat", choose: () => setLeft("chat") },
      {
        testid: "tab-search",
        label: "Search",
        selected: left === "search",
        choose: () => setLeft("search"),
      },
      {
        testid: "tab-entities",
        label: "Entities",
        selected: left === "entities",
        choose: () => setLeft("entities"),
      },
      {
        testid: "tab-identities",
        label: "Identities",
        selected: left === "identities",
        choose: () => setLeft("identities"),
      },
      {
        testid: "tab-links",
        label: "Links",
        selected: left === "links",
        choose: () => setLeft("links"),
      },
      {
        testid: "tab-chains",
        label: "Chains",
        selected: left === "chains",
        choose: () => setLeft("chains"),
      },
      { testid: "rail-open", label: "Tables", selected: null, choose: () => setRailOpen(true) },
    ],
    [left],
  );
  usePublishViewTabs(tabs);

  return (
    <div className="workspace">
      <section className="pane left">
        {/* Not rendered on a phone at all. Two stacked navigation strips — the topbar
            and this one — took 237px of an 800px viewport there, measured, before
            either had said anything; below the breakpoint these entries are the menu's
            `View` section instead. Hiding rather than dropping the strip would leave
            every `tab-*` testid matching two elements, one of them unclickable. */}
        {!sheet && (
          <nav className="tabs" role="tablist">
            {tabs
              .filter((tab) => tab.selected !== null)
              .map((tab) => (
                <button
                  key={tab.testid}
                  role="tab"
                  aria-selected={tab.selected ?? false}
                  data-testid={tab.testid}
                  onClick={tab.choose}
                >
                  {tab.label}
                </button>
              ))}
          </nav>
        )}

        {left === "chat" && (
          <Chat datasetId={datasetId} onBuildArtifact={onBuildArtifact} />
        )}
        {left === "search" && <Search datasetId={datasetId} />}
        {left === "entities" && <Entities datasetId={datasetId} />}
        {left === "identities" && <Identities datasetId={datasetId} />}
        {left === "links" && <Links datasetId={datasetId} />}
        {left === "chains" && <Chains datasetId={datasetId} />}
      </section>

      <EvidencePanel datasetId={datasetId} />

      {sheet && railOpen && (
        <div
          className="sheet-backdrop"
          data-testid="rail-backdrop"
          aria-hidden="true"
          onClick={closeRail}
        />
      )}
      <ArtifactRail
        datasetId={datasetId}
        seed={seed}
        onSeedUsed={onSeedUsed}
        open={railOpen}
        onClose={closeRail}
      />
    </div>
  );
}
